// Whether a layer is a cut-out (spec G13b, PR E2; live's `layer-bitmap.ts`): any pixel less than
// fully opaque asks for Bria's removal after the edit, since the edit model returns none. A
// picture that cannot be read counts as opaque, as live's: the edit still runs.
import { useQuery } from "@tanstack/react-query";

import { fetchSignedFile } from "../../../api/download";

const OPAQUE = 255;

async function readTransparency(url: string, signal: AbortSignal): Promise<boolean> {
  try {
    const bitmap = await createImageBitmap(await fetchSignedFile(url, signal));
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext("2d");
    if (context === null) {
      bitmap.close();
      return false;
    }
    context.drawImage(bitmap, 0, 0);
    bitmap.close();
    const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
    // Every fourth byte is a pixel's alpha; the first not fully opaque settles it.
    for (let at = 3; at < data.length; at += 4) {
      if (data[at]! < OPAQUE) return true;
    }
    return false;
  } catch {
    if (signal.aborted) throw signal.reason;
    return false;
  }
}

/** How a picture's pixels are read; a test's page cannot, so a test replaces it. */
export const TRANSPARENCY = { read: readTransparency };

/** Whether the picture at `url` has a pixel that is not fully opaque, read once per URL. */
export function useTransparency(url: string) {
  const query = useQuery({
    queryKey: ["layer-transparency", url],
    queryFn: ({ signal }) => TRANSPARENCY.read(url, signal),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
  return { transparent: query.data ?? false, checking: query.isPending };
}
